import { useCallback, useRef, useState } from 'react';
import useSSEStream from './useSSEStream';
import { verifyEmails as verifyEmailsAPI } from '../services/emailService';
import { useInsufficientCredits } from '../contexts/InsufficientCreditsContext';

/**
 * Custom hook for orchestrating email operations
 * Handles verification, drafting preview, and approval of email drafts
 * Bridges between CampaignChatInterface and VirtualizedPersonaResults
 * 
 * @param {string} campaignId - Campaign ID
 * @param {string} versionId - Version ID
 * @param {Function} onStatusUpdate - Callback for status updates
 * @param {Function} onDataUpdate - Callback for data updates (field definitions and values)
 * @param {Function} onComplete - Callback when operation completes
 * @param {Function} onError - Callback for errors
 * @param {Function} withCreditWarning - Optional credit warning wrapper function
 * @returns {Object} - Email operations and state
 */
const useEmailOrchestration = ({
  campaignId,
  versionId,
  onStatusUpdate,
  onDataUpdate,
  onComplete,
  onError,
  withCreditWarning = null
}) => {
  const emailOperationRef = useRef(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const { showInsufficientCreditsModal } = useInsufficientCredits();

  // SSE Stream for email draft generation
  const { startStream } = useSSEStream({
    onStatus: (status) => {
      if (onStatusUpdate) {
        onStatusUpdate(status, 'email_drafts');
      }
      
      // Also forward status events to data handler for banner visibility logic
      if (onDataUpdate) {
        onDataUpdate({
          type: 'status',
          data: status
        });
      }
    },
    onData: (data) => {
      // Forward data events for handling
      if (onDataUpdate) {
        if (data.event === 'custom_field_definition' || data.event === 'contact_field_definition') {
          // Email drafts create custom fields (subject and body)
          onDataUpdate({
            type: 'custom_field_definition',
            data: data.data
          });
        } else if (data.event === 'contacts_custom_field_update') {
          onDataUpdate({
            type: 'custom_field_update',
            data: data.data
          });
        }
      }
    },
    onComplete: async () => {
      const operationType = emailOperationRef.current;
      emailOperationRef.current = null;
      
      if (onComplete) {
        await onComplete(operationType);
      }
    },
    onError: (error) => {
      emailOperationRef.current = null;
      if (onError) {
        onError(error);
      }
    },
  });

  /**
   * Verify email addresses for contacts
   * Non-streaming API call with loading state
   * 
   * @param {Array<string>} contactIds - Optional array of contact elasticsearch_ids to verify
   * @returns {Promise} - Verification results
   */
  const verifyEmails = useCallback(async (contactIds = null) => {
    if (!campaignId || !versionId) {
      console.error('Campaign ID and Version ID required for email verification');
      return;
    }

    const performVerification = async () => {
      try {
        const response = await verifyEmailsAPI(campaignId, versionId, contactIds);
        
        // Check if this is a 403 signup-required response (API interceptor resolves instead of rejects)
        if (response.status === 403 || response.requiresSignup) {
          // Signup modal already triggered by API interceptor, don't dispatch events or show success
          return;
        }
        
        // Dispatch event to update header credit count
        window.dispatchEvent(new Event('verificationCompleted'));
        
        // Notify completion
        if (onComplete) {
          await onComplete('verification');
        }
        
        return response.data;
      } catch (error) {
        console.error('Error verifying emails:', error);
        
        // Handle insufficient credits error (402) - show modal instead of toast
        if (error.response?.status === 402) {
          const errorDetail = error.response?.data?.detail || 'You do not have enough credits for email verification.';
          showInsufficientCreditsModal(errorDetail);
        } 
        // Handle trial user restriction (403) - signup modal is shown by API interceptor, no toast
        else if (error.response?.status === 403) {
          // Silently handle - signup modal will be shown by the API interceptor
        }
        else if (onError) {
          onError(error);
        }
        throw error;
      }
    };

    // Show spinner immediately — covers both credit-check phase and actual verification
    setIsVerifying(true);
    try {
      if (withCreditWarning) {
        return await withCreditWarning(
          'email_verification',
          {
            campaign_id: campaignId,
            version_id: versionId,
            contact_ids: contactIds,
          },
          performVerification
        );
      } else {
        return await performVerification();
      }
    } finally {
      setIsVerifying(false);
    }
  }, [campaignId, versionId, onComplete, onError, showInsufficientCreditsModal, withCreditWarning]);

  /**
   * Preview email drafts (5 samples)
   * 
   * @param {Object} draftingParams - Drafting parameters (sender_first_name, sender_last_name, user_instructions)
   * @param {string} configId - Config ID for iteration (optional)
   */
  const previewEmailDrafts = useCallback(async (draftingParams, configId = null) => {
    if (!campaignId || !versionId) {
      console.error('Campaign ID and Version ID required for email drafting');
      return;
    }
    
    if (!draftingParams || !draftingParams.sender_first_name || !draftingParams.sender_last_name) {
      console.error('Sender name required for email drafting');
      return;
    }
    
    // Mark this as a preview operation
    emailOperationRef.current = 'preview';

    const performPreview = async () => {
      // Start SSE stream for email draft preview generation
      await startStream(
        `/api/emails/${campaignId}/draft-emails/preview/stream`,
        {
          method: 'POST',
          body: {
            version_id: versionId,
            drafting_params: draftingParams,
            config_id: configId,
          },
        }
      );
    };

    // If credit warning wrapper provided, use it; otherwise execute directly
    if (withCreditWarning) {
      return withCreditWarning(
        'email_draft',
        {
          campaign_id: campaignId,
          version_id: versionId,
          config_id: configId,
          qty: 5, // NUM_SAMPLES for preview
        },
        performPreview
      );
    }
    return performPreview();
  }, [campaignId, versionId, startStream, withCreditWarning]);

  /**
   * Approve email draft preview and run full generation
   * 
   * @param {string} configId - Config ID to approve
   */
  const approveEmailDrafts = useCallback(async (configId) => {
    if (!configId || !campaignId) return;
    
    // Mark this as an approval operation
    emailOperationRef.current = 'approval';

    // Credit warning is always handled by the caller (handleApprovePreview) before
    // reaching here, so just start the stream directly.
    await startStream(
      `/api/emails/${campaignId}/draft/configs/${configId}/approve/stream`,
      {
        method: 'POST',
      }
    );
  }, [campaignId, startStream]);

  return {
    verifyEmails,
    previewEmailDrafts,
    approveEmailDrafts,
    isVerifying,
    isGenerating: emailOperationRef.current !== null,
    currentOperation: emailOperationRef.current,
  };
};

export default useEmailOrchestration;
